import { useLayoutEffect, useRef } from 'react';
import { Check } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { DisabledHintTooltip } from '@ooh/ui/components/disabled-hint-tooltip';
import { fmtPln } from './client-offer-review-format';
import { ClientOfferReviewCustomPeriodsPopover } from './client-offer-review-custom-periods-popover';
import type {
  ClientOfferReviewCustomPeriodItem,
  ClientOfferReviewFooterBarLabels,
} from './client-offer-review-types';

/**
 * Fixed footer action bar — hidden entirely when readOnly. Accept only: the
 * whole-offer reject action was removed (offer-versioning-diff /
 * Version-Specific Reject Button UI, REMOVED) — withdrawing an acceptance is
 * exclusively a staff action.
 */
export function ClientOfferReviewFooterBar({
  visible,
  onHeightChange,
  accCount,
  rejCount,
  pendCount,
  accValue,
  versionNumber,
  readOnly,
  roHint,
  onAccept,
  customPeriodItems,
  onRestoreDefaultPeriods,
  onSelectUnit,
  labels,
  periodInquiry,
}: {
  visible: boolean;
  onHeightChange?: (height: number) => void;
  accCount: number;
  rejCount: number;
  pendCount: number;
  accValue: number;
  versionNumber: number;
  readOnly: boolean;
  roHint: string;
  onAccept: () => void;
  /** Units whose period selection differs from the proposed default. Empty -> no chip. */
  customPeriodItems: ClientOfferReviewCustomPeriodItem[];
  onRestoreDefaultPeriods: () => void;
  onSelectUnit: (unitId: string) => void;
  labels: ClientOfferReviewFooterBarLabels;
  /** "Wyślij zapytanie" period-inquiry action — present only on interactive routes (SSO + public). */
  periodInquiry?: { submitLabel: string; onSubmit: () => void; isPending: boolean };
}) {
  const footerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const footer = footerRef.current;
    if (!visible || !footer) return;

    const reportHeight = () => {
      const height = Math.ceil(footer.getBoundingClientRect().height);
      if (height > 0) onHeightChange?.(height);
    };
    reportHeight();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(reportHeight);
    observer.observe(footer);
    return () => observer.disconnect();
  }, [onHeightChange, visible]);

  if (!visible) return null;

  // offer-versioning-diff / Version-Specific Accept Button UI: distinct from `readOnly`
  // (preview mode) — a nothing-accepted interactive view is actionable-but-currently-
  // disabled, not read-only. `readOnly` wins when both apply (see roHint's existing
  // "preview mode" contract, unchanged).
  const nothingAccepted = accCount === 0;
  const disabled = readOnly || nothingAccepted;
  const hint = readOnly
    ? roHint
    : nothingAccepted
      ? labels.acceptDisabledNoAcceptedHint
      : undefined;

  return (
    <div
      ref={footerRef}
      className="fixed inset-x-0 bottom-0 z-40 flex flex-wrap items-center gap-4 border-t border-border bg-background/95 px-6 py-3 shadow-footer backdrop-blur-md"
      data-testid="offer-review-footer"
    >
      <ClientOfferReviewCustomPeriodsPopover
        items={customPeriodItems}
        onRestoreDefaultPeriods={onRestoreDefaultPeriods}
        onSelectUnit={onSelectUnit}
        labels={labels}
        periodInquiry={periodInquiry}
      />
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-xs text-muted-foreground">{labels.disclaimer}</span>
        <span className="text-xs tabular-nums">
          {labels.summary(accCount, rejCount, pendCount, fmtPln(accValue) ?? '')}
        </span>
      </div>
      <div className="ml-auto flex gap-2.5">
        <DisabledHintTooltip disabled={disabled} hint={hint}>
          <Button
            type="button"
            variant="success"
            onClick={onAccept}
            disabled={disabled}
            title={hint}
            data-testid="footer-accept-offer"
            className="inline-flex h-11 items-center gap-2 rounded-lg px-6 text-sm font-semibold"
          >
            <Check className="h-4 w-4" strokeWidth={2.4} />
            {labels.accept(versionNumber)}
          </Button>
        </DisabledHintTooltip>
      </div>
    </div>
  );
}
